import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { api, setAccessToken, getAccessToken, setOnUnauthorized } from '@/lib/api';
import { resetGoogleMaps } from '@/lib/useGoogleMaps';
import { resetRegionalCache } from '@/lib/regional';
import { toast } from 'sonner';

export interface RaptorUser {
  id: string;
  email: string;
  full_name: string;
  role_id: string;
  role_name?: string;
  active: boolean;
  must_reset_password: boolean;
  twofa_enabled: boolean;
  context: 'insurer' | 'broker' | 'client' | 'master';
  insurer_id?: string | null;
  insurer_name?: string | null;
  broker_id?: string | null;
  client_id?: string | null;
  last_login_at?: string | null;
  phone?: string | null;
}

export interface PermissionCell {
  resource: string;
  actions: Record<string, boolean>;
  scope: string;
}
export interface Role {
  _id?: string;
  id?: string;
  key?: string;
  name: string;
  description?: string;
  is_builtin?: boolean;
  is_phase2?: boolean;
  permissions: PermissionCell[];
}

export type LoginResult =
  | { status: 'ok'; user: RaptorUser }
  | { status: '2fa'; mfaToken: string; phoneOnFile: boolean; phoneHint?: string; challengeId?: string; expiresMin?: number };

interface AuthState {
  user: RaptorUser | null;
  role: Role | null;
  loading: boolean;
  impersonating: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verify2fa: (mfaToken: string, challengeId: string, code: string) => Promise<RaptorUser>;
  send2faPhone: (mfaToken: string, phone: string) => Promise<{ challengeId: string; phoneHint: string; expiresMin?: number }>;
  resend2fa: (mfaToken: string) => Promise<{ challengeId: string; phoneHint?: string; expiresMin?: number }>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  setForceResetDone: () => void;
  switchIntoInsurer: (insurerId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | undefined>(undefined);

// The Master Admin's own access token, stashed the moment they switch into an
// insurer so exiting impersonation can restore it instantly without depending
// on a network round-trip (the refresh cookie is also a fallback).
let cachedMasterToken: string | null = null;

// Tell the BrandingProvider to re-resolve branding for the current auth
// context (login / tenant switch / exit impersonation / logout).
const signalBrandingRefresh = (): void => {
  resetGoogleMaps();  // re-resolve the per-tenant Google Maps browser key too
  resetRegionalCache();  // drop cached timezone/locale so it re-fetches
  try { window.dispatchEvent(new Event('raptor:branding-refresh')); } catch { /* ignore */ }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<RaptorUser | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [impersonating, setImpersonating] = useState<boolean>(false);
  const idleMinRef = useRef<number>(0);  // 0 = disabled

  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(null); setRole(null);
      // silent redirect handled by route guard
    });
    // try silent refresh
    (async () => {
      try {
        const r = await api.post('/auth/refresh');
        setAccessToken(r.data.access_token);
        const me = await api.get('/auth/me');
        setUser(me.data.user);
        setRole(me.data.role);
        setImpersonating(Boolean(me.data.user?.impersonated_by));
        idleMinRef.current = Number(me.data.security?.idle_timeout_min) || 0;
        signalBrandingRefresh();
      } catch { /* not logged in */ }
      finally { setLoading(false); }
    })();
  }, []);

  const finalizeSession = async (accessToken: string): Promise<RaptorUser> => {
    setAccessToken(accessToken);
    const me = await api.get('/auth/me');
    setUser(me.data.user);
    setRole(me.data.role);
    setImpersonating(Boolean(me.data.user?.impersonated_by));
    idleMinRef.current = Number(me.data.security?.idle_timeout_min) || 0;
    signalBrandingRefresh();
    return me.data.user as RaptorUser;
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const r = await api.post('/auth/login', { email, password });
    if (r.data?.requires_2fa) {
      return {
        status: '2fa',
        mfaToken: r.data.mfa_token,
        phoneOnFile: Boolean(r.data.phone_on_file),
        phoneHint: r.data.phone_hint,
        challengeId: r.data.challenge_id,
        expiresMin: r.data.expires_min,
      };
    }
    const u = await finalizeSession(r.data.access_token);
    return { status: 'ok', user: u };
  };

  const verify2fa = async (mfaToken: string, challengeId: string, code: string): Promise<RaptorUser> => {
    const r = await api.post('/auth/login/verify-otp', { mfa_token: mfaToken, challenge_id: challengeId, code });
    return finalizeSession(r.data.access_token);
  };

  const send2faPhone = async (mfaToken: string, phone: string): Promise<{ challengeId: string; phoneHint: string; expiresMin?: number }> => {
    const r = await api.post('/auth/login/set-phone', { mfa_token: mfaToken, phone });
    return { challengeId: r.data.challenge_id, phoneHint: r.data.phone_hint, expiresMin: r.data.expires_min };
  };

  const resend2fa = async (mfaToken: string): Promise<{ challengeId: string; phoneHint?: string; expiresMin?: number }> => {
    const r = await api.post('/auth/login/resend-otp', { mfa_token: mfaToken });
    return { challengeId: r.data.challenge_id, phoneHint: r.data.phone_hint, expiresMin: r.data.expires_min };
  };

  const logout = async (): Promise<void> => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null); setRole(null);
    setImpersonating(false);
    signalBrandingRefresh();
  };

  // Auto sign-out after genuine inactivity (admin-configured idle timeout).
  // Activity-based, not a blind timer: the countdown only runs while the
  // Raptor tab is actually in the foreground. Switching to another tab/app
  // PAUSES it (no false logout while you work elsewhere), and returning to
  // the tab resets the countdown — so a short tab-switch never signs you out.
  useEffect(() => {
    if (!user || !idleMinRef.current) return;
    const idleMs = idleMinRef.current * 60_000;
    let lastActivity = Date.now();
    const bump = (): void => { lastActivity = Date.now(); };
    const onVisible = (): void => { if (document.visibilityState === 'visible') lastActivity = Date.now(); };
    const check = (): void => {
      // Never sign out while the tab is hidden/backgrounded — only count
      // inactivity that happens while the user is actually on the page.
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity >= idleMs) {
        void logout();
        toast('Signed out due to inactivity', { description: `No activity for ${idleMinRef.current} minute(s).` });
      }
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const interval = setInterval(check, 15_000);
    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshMe = async (): Promise<void> => {
    const me = await api.get('/auth/me');
    setUser(me.data.user); setRole(me.data.role);
    setImpersonating(Boolean(me.data.user?.impersonated_by));
  };

  const switchIntoInsurer = async (insurerId: string): Promise<void> => {
    cachedMasterToken = getAccessToken();  // remember Master identity for a clean exit
    const r = await api.post('/master/switch', { insurer_id: insurerId });
    setAccessToken(r.data.access_token);
    // fetch the impersonated user profile
    const me = await api.get('/auth/me');
    setUser(me.data.user);
    setRole(me.data.role);
    setImpersonating(true);
    signalBrandingRefresh();
  };

  const setForceResetDone = (): void => {
    if (user) setUser({ ...user, must_reset_password: false });
  };

  // Leave an impersonated insurer and restore the Master Admin session. We
  // first restore the Master token cached at switch time (instant, no network);
  // if that token has since expired, fall back to a silent refresh (the
  // Master's refresh cookie survives a switch). Throws if neither path works
  // so the caller can surface an error instead of silently redirecting.
  const exitImpersonation = async (): Promise<void> => {
    const restore = async (tok: string | null | undefined): Promise<boolean> => {
      if (!tok) return false;
      setAccessToken(tok);
      try {
        const me = await api.get('/auth/me');
        if (me.data?.user?.context !== 'master') return false;
        setUser(me.data.user); setRole(me.data.role); setImpersonating(false);
        return true;
      } catch { return false; }
    };
    let ok = await restore(cachedMasterToken);
    if (!ok) {
      const r = await api.post('/auth/refresh');
      ok = await restore(r.data?.access_token);
    }
    if (!ok) throw new Error('Could not restore the Master Admin session.');
    cachedMasterToken = null;
    signalBrandingRefresh();
  };

  return (
    <AuthCtx.Provider value={{ user, role, loading, impersonating, login, verify2fa, send2faPhone, resend2fa, logout, refreshMe, setForceResetDone, switchIntoInsurer, exitImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const hasPermission = (role: Role | null, resource: string, action: string): boolean => {
  if (!role) return false;
  const cell = role.permissions.find((p) => p.resource === resource);
  return Boolean(cell?.actions?.[action]);
};
